import {
  BenefitBackground,
  FieldTypeAsset,
  FieldTypeHTML,
  FieldTypeLink,
} from '@allesauto/utils/consts';
import {
  createMarkup,
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import {
  ITypeListMarker,
  ITypeListMarkerColor,
} from '@allesauto/utils/storyblok';
import { ChevronDown24 } from '@carbon/icons-react';
import { renderRichText } from '@storyblok/react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React, { FC } from 'react';

import CheckList from '../checklistContainer/checklist/CheckList';
import NextImage from '../nextImage';
import TextLink from '../textLink';

interface StepComponentInterface {
  title: string;
  text?: FieldTypeHTML | string;
  checklist?: string[];
  imageLink: FieldTypeAsset;
  link?: FieldTypeLink;
  linkLabel?: string;
  listItemMarker?: ITypeListMarker;
  listItemMarkerColor?: ITypeListMarkerColor;
}

export interface IStepComponent {
  stepComponent: StepComponentInterface[];
  customClassName?: string;
  component: string;
  _uid: string;
  _editable?: string;
}

const StepComponent: FC<IStepComponent> = (props: IStepComponent) => {
  const { stepComponent, customClassName } = props;

  const className = {
    mainWrapper: clsx(
      'grid grid-cols-24 sm:pt-[40px] md:pt-[80px]',
      customClassName
    ),
    mainWrapperIntro: 'grid grid-cols-24 sm:pt-[82px] md:pt-100px',
    introClass: 'flex sm:flex-row md:flex-col',
    componentTitle:
      'font-headline sm:pt-20px sm:pb-15px md:py-30px text-headline3',
    componentBody: 'rich-text leading-6 sm:pb-20px md:pb-30px',
    stepperText:
      'h-full sm:flex md:block sm:items-center sm:pl-25px sm:pr-25px md:px-0 sm:text-[28px] md:text-[52px]',
    textLinkClass: 'flex sm:flex-col md:flex-row',
    chevronDown: 'absolute bottom-[24px] opacity-30 sm:hidden md:block',
  };

  const returnFormatStep = (step: number) => {
    return step < 9 ? '0' + ++step : ++step;
  };

  const returnComponentClass = (stepNo: number) => {
    const colorsByStep =
      stepNo % 3 === 1
        ? {
            bgC: 'bg-green60 text-purple80 monochrome:bg-grey20 monochrome:text-grey90',
            chevronDown: 'text-purple80',
          }
        : stepNo % 3 === 2
        ? {
            bgC: 'bg-purple80 text-green60 monochrome:bg-grey90 monochrome:text-white',
            chevronDown: 'text-white',
          }
        : {
            bgC: 'bg-grey80 text-green60 monochrome:bg-grey60 monochrome:text-white',
            chevronDown: 'text-white',
          };
    return stepNo % 2
      ? {
          bgColor:
            colorsByStep.bgC +
            ' font-headline sm:h-full md:h-2/3 sm:pt-0 md:pt-10px text-center text-headline1 flex sm:justify-end md:justify-center relative',
          alignmentPic:
            'col-start-2 sm:col-end-24 md:col-end-12 order-1 relative aspect-[16/10]',
          alignmentText: 'sm:col-start-2 md:col-start-15 col-end-24 order-2',
          stepperClass:
            'sm:col-start-2 sm:col-end-16 md:col-start-12 md:col-end-14 sm:order-0 md:order-2 md:h-auto sm:h-[50px]',
          chevronDown:
            'absolute bottom-[24px] opacity-30 text-black sm:hidden md:block',
        }
      : {
          bgColor:
            colorsByStep.bgC +
            ' font-headline sm:h-full md:h-2/3 sm:pt-0 md:pt-10px text-center text-headline1 flex sm:justify-start md:justify-center relative',
          alignmentPic:
            'sm:col-start-2 md:col-start-14 col-end-24 sm:order-1 md:order-2 relative aspect-[16/10]',
          alignmentText:
            'col-start-2 sm:col-end-24 md:col-end-11 sm:order-2 md:order-1',
          stepperClass:
            'sm:col-start-10 sm:col-end-24 md:col-start-12 md:col-end-14 sm:order-0 md:order-2 md:h-auto sm:h-[50px]',
          chevronDown:
            'absolute bottom-[24px] opacity-30 text-white sm:hidden md:block',
        };
  };

  return (
    <div {...sbEditable(props)} className="bg-white">
      {stepComponent &&
        stepComponent.map((step, index) => {
          const objClass = returnComponentClass(index + 1);
          const url = step.link && getUrlFromStoryblokLink(step.link);
          const stepLink = (url && parseSlashToURL(url)) || '';

          const imageUrl = step?.imageLink?.filename ?? null;

          return (
            <div
              key={`step-component-${index}`}
              className={className.mainWrapper}
            >
              <div className={objClass.stepperClass}>
                <div className={objClass?.bgColor}>
                  <p className={className.stepperText}>
                    {returnFormatStep(index)}
                  </p>
                  <ChevronDown24
                    className={clsx(
                      className.chevronDown,
                      +objClass.chevronDown
                    )}
                  />
                </div>
              </div>
              {imageUrl && (
                <div className={objClass?.alignmentPic}>
                  <NextImage
                    baseUrls={
                      process.env.IMAGE_BASE_URL ||
                      process.env.NEXT_PUBLIC_IMAGE_BASE_URL ||
                      'https://d24sg1b3ryrxs4.cloudfront.net'
                    }
                    alt={step?.imageLink?.alt}
                    layout="fill"
                    src={imageUrl}
                    objectFit="cover"
                    loader={({ src, width, quality }) => {
                      return `${src}?w=${width}&q=${quality || 75}`;
                    }}
                  />
                </div>
              )}
              <div className={objClass?.alignmentText}>
                <p
                  className={clsx(
                    className.componentTitle,
                    step.checklist && '!pb-20px'
                  )}
                >
                  {step.title}
                </p>
                {step.text && (
                  <div
                    className={clsx(
                      className.componentBody,
                      `rich-text-list-marker-${
                        step.listItemMarker ? step.listItemMarker : 'bullet'
                      }-${
                        step.listItemMarkerColor
                          ? step.listItemMarkerColor
                          : 'purple'
                      }`
                    )}
                    dangerouslySetInnerHTML={createMarkup(
                      (step.text as unknown as FieldTypeHTML).type
                        ? renderRichText(step.text as FieldTypeHTML)
                        : (step.text as unknown as string)
                    )}
                  ></div>
                )}
                {step.checklist && (
                  <div className="sm:-ml-30px lg:-ml-40px">
                    <CheckList
                      list={{
                        listItems: step.checklist,
                        background: BenefitBackground.WHITE,
                        customClassName: {
                          wrapperClassName: '!pt-0',
                          checkItemClassName: 'mb-[27px]',
                          checkMarkIconClassName: 'text-green60',
                        },
                      }}
                    />
                  </div>
                )}
                <div className={className.textLinkClass}>
                  {step.linkLabel && stepLink && (
                    <TextLink
                      text={step.linkLabel}
                      href={stepLink}
                      customClassName={'pr-[32px] sm:pb-15px md:pb-0'}
                      key={`step-component-text-link`}
                      target={step.link?.target}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default StepComponent;
